import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core';

import { HisNotificationService, HisSearchGroup,
    HisUiService, HisAuthService, HisGlobalDataService, HisSearchSelectPopupComponent, HisNavigationService } from 'hisng';

import { DATA_SERVICE_KEY_CHILD_PUBLICATION } from 'app/fs/res/publication/model/constants';

import { PublicationAuthService } from 'app/fs/res/publication/service/publication-auth.service';
import { PublicationService } from 'app/fs/res/publication/service/publication.service';
import { PublicationType } from 'app/fs/res/publication/model/publication-type';
import { Publication } from 'app/fs/res/publication/model/publication';
import { PublicationModuleOptions } from 'app/fs/res/publication/publication-module.options';

@Component({
    selector: 'res-publication-parent-plam',
    templateUrl: './publication-parent-plam.component.html',
    providers: [PublicationService]
})
export class PublicationParentPlamComponent implements OnChanges {

    public readonly moduleOptions = PublicationModuleOptions;

    @ViewChild('searchSelectPopup') searchDialog: HisSearchSelectPopupComponent;
    @Input() selectedParent: Publication;
    @Input('publication') set pub(publication: Publication) {
        this.publication = publication;
        this.setDefaultSearchGroup();
    }
    @Input() dDKey = '';
    @Input('publicationTypeChildParentMap') set publicationTypeMap(map: {[key: string]: PublicationType[]}) {
        this.publicationTypeChildParentMap = map;
        this.setDefaultSearchGroup();
    }
    @Output() selectedParentChange = new EventEmitter();
    @Output() navigateToCreateParentOnNextSave = new EventEmitter<boolean>();
    @Output() navigateToEditParentOnNextSave = new EventEmitter<boolean>();
    public publication: Publication;
    publicationTypeChildParentMap: {[key: string]: PublicationType[]};

    canEditParentPublication: boolean;
    public filteredParents: any[];
    saveWithoutMandatoryFieldsAuthorityLevel;
    statusAuthority;
    defaultSearchGroup: HisSearchGroup;
    hiddenSearchCriteriaPaths: string[];

    constructor(
        public auth: HisAuthService,
        public hisUiService: HisUiService,
        private globalDataService: HisGlobalDataService,
        private notificationService: HisNotificationService,
        public publicationService: PublicationService,
        private navigationService: HisNavigationService,
        private publicationAuthService: PublicationAuthService
    ) {
        this.saveWithoutMandatoryFieldsAuthorityLevel = this.publicationAuthService.getSaveWithoutMandatoryFieldsAuthorityLevel();
        this.statusAuthority = this.publicationAuthService.hasStatusAuthority();
    }

    /**
     * ngOnChanges() reloads the selected parent to avoid loosing identifers after saving the publication.
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedParent && this.selectedParent) {
            this.publicationService.getPublicationById(this.selectedParent.id).subscribe(
                ( parentPublication: Publication ) => {
                    this.selectedParent = parentPublication;
                });

            this.publicationService.canEditPublication(this.selectedParent.id).subscribe(
	            (canEdit: boolean) => {
			    this.canEditParentPublication = canEdit;
                });
        }
        this.setDefaultSearchGroup();
    }

    private setDefaultSearchGroup() {
        if (this.publicationTypeChildParentMap && this.publication?.publicationType?.id) {
            const parents = this.publicationTypeChildParentMap[this.publication.publicationType.id];
            this.defaultSearchGroup = {
                criteria: [{
                    field: 'Publication.publicationType.id',
                    value: parents.map((pT: any) => pT.id).join(','),
                    not: false,
                    operator: null
                }],
                groupLabel: 'I18nValue.basicData',
                criteriaGroupAnd: false,
                criteriaGroups: []
            };
            this.hiddenSearchCriteriaPaths = ['Publication.publicationType.id'];
        } else {
            this.defaultSearchGroup = null;
            this.hiddenSearchCriteriaPaths = null;
        }
    }

    // autocomplete functions #########################################################################################################

    public filterParent(event: { query: string }) {
        // load publications matching the query without identifiers
        this.publicationService.getPublicationsByCriteria(event.query, this.publicationTypeChildParentMap[this.publication.publicationType.id]
            .map(pT => pT.id))
            .subscribe(( publications: Publication[] ) => this.filteredParents = publications );
    }

    public onClear(): void {
        this.selectedParent = null;
        this.selectedParentChange.emit(null);
    }

    // manipulation methods ########################################################################################

    /**
     * Set parent selected by autocomplete.
     */
    public onSelectByAutocomplete() {
        if (!this.selectedParent) {
            this.notificationService.errorI18nRes('fs.res.publication.error.parent');
        } else {
            // reload selected parent to obtain identifiers as well
            this.loadSelectedParentWithIdentifiers(this.selectedParent.id, true);
        }
    }

    /**
     * Set parent selected by his-search-select-popup single select.
     */
    public onSelectBySearchPopup($event: any[]) {
        console.log('Set parent publication', $event[0]);
        // convert search result into Publication object
        let publicationId: number = parseInt($event[0].id, 10);
        this.loadSelectedParentWithIdentifiers(publicationId, true);
    }

    private loadSelectedParentWithIdentifiers(publicationId: number, emitChange: boolean) {
        this.publicationService.getPublicationById(publicationId).subscribe(
            ( parentPublication: Publication ) => {
                this.selectedParent = parentPublication;
                if (emitChange) {
                    this.selectedParentChange.emit(this.selectedParent);
                }
            });
    }

    public getParentList(): Publication[] {
        return this.selectedParent ? [this.selectedParent] : [];
    }

    public identifier(): string {
        let printName = '';
        if ( this.selectedParent.globalIdentifiers ) {
            let identifierType = this.selectedParent.globalIdentifiers[0];
            printName = `${identifierType.globalIdentifierType.text}: ${identifierType.identifier}`;
        }
        return printName;
    }

    navigateToParentPublicationIfNoUnsavedChanges(createNewPublication: boolean) {
        this.publicationService.checkIsDirty(this.publication).subscribe((isDirty: boolean) => {
            if (isDirty) {
                if (this.saveWithoutMandatoryFieldsAuthorityLevel === 2) {
                    this.notificationService.warnI18nRes(
                        this.statusAuthority ?
                            'fs.res.publication.Publication.notSavedInStatusDataEntryOrForValidation':
                            'fs.res.publication.Publication.notSavedAsDataEntryOrForValidation',
                        this.publication.publicationType.defaulttext
                    );
                } else if (this.saveWithoutMandatoryFieldsAuthorityLevel === 1) {
                    this.notificationService.warnI18nRes(
                        'fs.res.publication.Publication.notSavedAsDataEntry',
                        this.publication.publicationType.defaulttext
                    );
                }
                this.searchDialog.hideSearchFormResultComponent();
                this.navigateToCreateParentOnNextSave.emit(createNewPublication);
                this.navigateToEditParentOnNextSave.emit(!createNewPublication);
            } else {
                this.navigationService.setLastBackUrl( window.location.href );
                this.globalDataService.setData( DATA_SERVICE_KEY_CHILD_PUBLICATION, this.publication );
                if (createNewPublication) {
                    this.publicationService.navigateToCreateParentPublication(false);
                } else {
                    this.publicationService.navigateToEditParentPublication(false, this.selectedParent.id);
                }
            }
        });
    }
}
